/** The backup facts the native stop of a stand-down reported. */
export interface StandDownStopFacts {
  finishSucceeded: boolean;
  backupUploaded: boolean;
  backupPendingAsync: boolean;
  backupDeferred: boolean;
  backupRetained: boolean;
}

/** How one start ended. */
export type RecordingStartOutcome =
  /** It left nothing running that a stop must take down. */
  | { kind: 'none' }
  /** It published this native recording and owns its capture. */
  | { kind: 'live'; recordingId: string }
  /**
   * It was fenced and stopped what it started. `stop` is undefined when no upload
   * session was stopped; `stillStopping` says the native stop outlived its wait and still owns the WAVs.
   */
  | { kind: 'stoodDown'; stop: StandDownStopFacts | undefined; stillStopping: boolean };

/**
 * The settings the fencing stop sends Rust. A stand-down's session stop sends them
 * too, since Rust keeps the first stop of a recording and replays it to later ones.
 */
export interface StandDownStopSettings {
  detachedBackupMux: boolean;
  /** Same shape as the stop IPC's offline handover; undefined hands nothing over. */
  offlineHandover:
    | {
        templateId?: string;
        dictationType?: string;
        language?: string;
        outputLanguage?: string;
        noteStyle?: string;
      }
    | undefined;
}

/** What the stop that fenced a start asks of it. */
export interface RecordingStartFence {
  /** The stop is a WAV-writer failure, so the WAV must not be read. */
  quarantineWav: boolean;
  /** The stop's reason, which a stand-down's own session stop carries. */
  reason: 'userStop' | 'pause';
  /** Undefined when the stop sends Rust no settings beyond its reason. */
  stopSettings?: StandDownStopSettings;
}

/** A device-switch restart, or a start the user asked for. */
export type RecordingStartKind = 'restart' | 'user';

/** One start invocation, held by the start mutation for its whole run. */
export interface RecordingStartTicket {
  readonly kind: RecordingStartKind;
  /** The fence a stop put up while this start was starting, if any. */
  fence: () => RecordingStartFence | undefined;
  /** Reports how the start ended. The first call wins; later calls are ignored. */
  settle: (outcome: RecordingStartOutcome) => void;
}

interface InFlightStart {
  sessionId: string;
  kind: RecordingStartKind;
  fence: RecordingStartFence | undefined;
  settled: Promise<RecordingStartOutcome>;
}

const inFlight = new Set<InFlightStart>();
const fencingStopsBySession = new Map<string, Set<Promise<void>>>();
const liveRecordingIdsBySession = new Map<string, Set<string>>();

/** Registers one device-switch restart. Call it before the restart's first await. */
export function beginMidRecordingRestart(sessionId: string): RecordingStartTicket {
  return beginStart(sessionId, 'restart');
}

/**
 * Registers one user-initiated start. Call it before the start's first await.
 * A stop fences only the starts in flight when it lands.
 */
export function beginUserRecordingStart(sessionId: string): RecordingStartTicket {
  return beginStart(sessionId, 'user');
}

function beginStart(sessionId: string, kind: RecordingStartKind): RecordingStartTicket {
  let resolve: (outcome: RecordingStartOutcome) => void = () => {};
  const start: InFlightStart = {
    sessionId,
    kind,
    fence: undefined,
    settled: new Promise((r) => {
      resolve = r;
    }),
  };
  inFlight.add(start);
  let done = false;
  return {
    kind,
    fence: () => start.fence,
    settle: (outcome) => {
      if (done) return;
      done = true;
      inFlight.delete(start);
      // Only a restart's recording is a reconcile's to stop.
      if (outcome.kind === 'live' && kind === 'restart') {
        const ids = liveRecordingIdsBySession.get(sessionId) ?? new Set<string>();
        ids.add(outcome.recordingId);
        liveRecordingIdsBySession.set(sessionId, ids);
      }
      resolve(outcome);
    },
  };
}

/**
 * Fences only the session's restarts still starting, for a reconcile: a user start
 * then is a new recording, not a late one. Returns one promise per fenced restart.
 */
export function fenceMidRecordingRestarts(
  sessionId: string,
  fence: RecordingStartFence
): Promise<RecordingStartOutcome>[] {
  return fenceStarts(sessionId, fence, 'restart');
}

/**
 * Fences every start of the session still starting, user starts and restarts
 * alike, so each stands down. Returns one promise per fenced start.
 */
export function fenceRecordingStarts(
  sessionId: string,
  fence: RecordingStartFence
): Promise<RecordingStartOutcome>[] {
  return fenceStarts(sessionId, fence);
}

function fenceStarts(
  sessionId: string,
  fence: RecordingStartFence,
  onlyKind?: RecordingStartKind
): Promise<RecordingStartOutcome>[] {
  const pending: Promise<RecordingStartOutcome>[] = [];
  for (const start of inFlight) {
    if (start.sessionId !== sessionId) continue;
    if (onlyKind && start.kind !== onlyKind) continue;
    // Two stops may fence one start: a quarantine from either holds, and a user
    // stop outranks a pause, settings included; between equals the later one wins.
    // A fence with no settings keeps the ones an earlier fence carried.
    const previous = start.fence;
    const winner =
      previous?.reason === 'userStop' && fence.reason === 'pause' ? previous : fence;
    start.fence = {
      quarantineWav: fence.quarantineWav || previous?.quarantineWav === true,
      reason: winner.reason,
      stopSettings: winner.stopSettings ?? previous?.stopSettings,
    };
    pending.push(start.settled);
  }
  return pending;
}

/** The kinds of the session's starts still in flight, for a stop to log what it fences. */
export function inFlightRecordingStartKinds(sessionId: string): RecordingStartKind[] {
  return [...inFlight]
    .filter((start) => start.sessionId === sessionId)
    .map((start) => start.kind);
}

/**
 * Held by a stop that fenced a start, until its own session stop is done. Its
 * retries carry no epoch, so a start that claimed one meanwhile would be stopped.
 * Returns the release; later calls are ignored.
 */
export function holdRecordingStartsForStop(sessionId: string): () => void {
  let release: () => void = () => {};
  const held = new Promise<void>((r) => {
    release = r;
  });
  const holds = fencingStopsBySession.get(sessionId) ?? new Set<Promise<void>>();
  holds.add(held);
  fencingStopsBySession.set(sessionId, holds);
  return () => {
    release();
    holds.delete(held);
    if (holds.size === 0 && fencingStopsBySession.get(sessionId) === holds) {
      fencingStopsBySession.delete(sessionId);
    }
  };
}

/**
 * Settles once every fenced start of the session has and every stop that fenced
 * one is done; undefined when there is none, or when an unfenced start of the
 * session is still in flight and owns it. A new start waits on it rather than
 * being dropped behind a start that stands down.
 */
export function whenFencedStartsSettle(sessionId: string): Promise<void> | undefined {
  const starts = [...inFlight].filter((start) => start.sessionId === sessionId);
  if (starts.some((start) => !start.fence)) return undefined;
  const waits = [
    ...starts.map((start) => start.settled),
    ...(fencingStopsBySession.get(sessionId) ?? []),
  ];
  if (waits.length === 0) return undefined;
  return Promise.all(waits).then(() => undefined);
}

/** Whether a restart of the session published this native recording, or any when none is named. */
export function isMidRecordingRestartRecording(
  sessionId: string,
  recordingId?: string
): boolean {
  const ids = liveRecordingIdsBySession.get(sessionId);
  if (!ids) return false;
  return recordingId === undefined ? ids.size > 0 : ids.has(recordingId);
}

/**
 * The session's restart recordings are no longer a stop's to take: a user start
 * has claimed the session, or a stop already dealt with them. Read at stop time,
 * so it also disarms a stop still in flight.
 */
export function forgetMidRecordingRestarts(sessionId: string): void {
  liveRecordingIdsBySession.delete(sessionId);
}

/** Test-only: reset module state between cases. */
export function __resetMidRecordingRestarts(): void {
  inFlight.clear();
  liveRecordingIdsBySession.clear();
  fencingStopsBySession.clear();
}
